/* The guided level up: the four steps of the Leveling Up rules, one screen each, with
   the picks each advancement needs and what changes on the sheet. */

import { Fragment, useState } from 'react';
import type { MartialFormData } from './content/classes';
import { COMPANION_SHEET, EXPERIENCE_EXAMPLES } from './content/sheetText';
import { DomainCardSheet } from './cards/DomainCardSheet';
import { Catalog, classDomains } from './catalog';
import {
  Advancement, Character, CompanionTraining, deriveStats, LevelUp, Multiclass, parseTraits, spellcastTrait, TIER_ACHIEVEMENT_LEVELS, Trait, TRAITS,
} from './model';
import { COMPANION_DICE, COMPANION_RANGES, companionStats, hasCompanion, trainingOptions } from './companion';
import {
  AdvancementOption, advancementOptions, CARD_LEVEL_CAP, cardChoices, experienceList, knownStances, levelOf, levelUpProblems, markedTraits,
  ownedCards, picksLeft, subclassCards, tierOf,
} from './leveling';
import { Chips, Choice, TextField, toggle } from './BuilderParts';
import { capitalize, signed } from './SheetParts';
import { TraitButton } from './StepsNumbers';
import Dialog from './Dialog';

interface Props {
  character: Character;
  catalog: Catalog;
  onDone: (c: Character) => void;
  onClose: () => void;
  /** Reopens the last level, to make the choices it was finished without. */
  edit?: boolean;
}

type Stage = 'achievement' | 'advancements' | 'thresholds' | 'card' | 'stance' | 'companion';

const STAGE_TITLES: Record<Stage, string> = {
  achievement: 'Tier Achievement',
  advancements: 'Advancements',
  thresholds: 'Damage Thresholds',
  card: 'Domain Card',
  stance: 'Martial Stance',
  companion: 'Companion',
};


const ACHIEVEMENT_TEXT: Record<number, string> = {
  2: 'At level 2, you gain a new Experience at +2 and permanently increase your Proficiency by 1.',
  5: 'At level 5, you gain a new Experience at +2, permanently increase your Proficiency by 1, and clear any marked traits.',
  8: 'At level 8, you gain a new Experience at +2, permanently increase your Proficiency by 1, and clear any marked traits.',
};

const EMPTY_MULTICLASS: Multiclass = { className: '', domain: '', subclass: '' };

export default function LevelUpFlow({ character, catalog, onDone, onClose, edit }: Props) {
  const last = edit ? character.levelUps[character.levelUps.length - 1] : undefined;
  const lastMulticlassed = !!last?.advancements.some((a) => a.kind === 'multiclass');
  // The character before this level: everything below works the same for a new or a reopened level.
  const c: Character = last
    ? { ...character, levelUps: character.levelUps.slice(0, -1), multiclass: lastMulticlassed ? null : character.multiclass }
    : character;
  const level = levelOf(c) + 1;
  const achievement = TIER_ACHIEVEMENT_LEVELS.includes(level);
  // A Beastbound Ranger also trains their companion, as the Ranger Companion sheet asks.
  const companion = hasCompanion(c, catalog);
  // A Martial Artist learns a stance each level (Stance Fighter).
  const martialForm = catalog.cls(c.className)?.subclasses?.[c.subclass]?.martialForm;
  const stages: Stage[] = [
    ...(achievement ? ['achievement' as const] : []),
    'advancements', 'thresholds', 'card',
    ...(martialForm ? ['stance' as const] : []),
    ...(companion ? ['companion' as const] : []),
  ];
  const [stage, setStage] = useState(0);
  const [draft, setDraft] = useState<LevelUp>(
    last ?? {
      level, experience: achievement ? '' : undefined, advancements: [], card: '',
      companionExperience: companion && achievement ? '' : undefined,
    },
  );
  const [multiclass, setMulticlass] = useState<Multiclass>((lastMulticlassed && character.multiclass) || EMPTY_MULTICLASS);
  const [confirming, setConfirming] = useState(false);

  const multiclassing = draft.advancements.some((a) => a.kind === 'multiclass');
  // An exchange counts only once both cards are chosen.
  const levelUp = draft.swap && !draft.swap.to ? { ...draft, swap: undefined } : draft;
  const next: Character = { ...c, levelUps: [...c.levelUps, levelUp], multiclass: multiclassing ? multiclass : c.multiclass };
  const before = deriveStats(c, catalog);
  const after = deriveStats(next, catalog);
  const left = picksLeft(draft.advancements);
  // Functional updates, so quick clicks never work from a stale draft.
  const update = (patch: Partial<LevelUp>) => setDraft((d) => ({ ...d, ...patch }));
  const editAdvancements = (fn: (list: Advancement[]) => Advancement[]) => setDraft((d) => ({ ...d, advancements: fn(d.advancements) }));
  const setAdvancement = (i: number, fn: (a: Advancement) => Partial<Advancement>) =>
    editAdvancements((list) => list.map((a, k) => (k === i ? { ...a, ...fn(a) } : a)));

  const problems = levelUpProblems(draft, multiclass, companion, !!martialForm);

  const finish = () => onDone(next);
  const undo = () => {
    const last = c.levelUps[c.levelUps.length - 1];
    const undoesMulticlass = last?.advancements.some((a) => a.kind === 'multiclass');
    onDone({ ...c, levelUps: c.levelUps.slice(0, -1), multiclass: undoesMulticlass ? null : c.multiclass });
  };

  const current = stages[stage];
  const footer = (
    <>
      <span className="cb-dialog__progress">Step {stage + 1} of {stages.length}</span>
      <button type="button" className="cb-btn" disabled={stage === 0} onClick={() => setStage(stage - 1)}>Back</button>
      {stage < stages.length - 1 ? (
        <button type="button" className="cb-btn cb-btn--primary" onClick={() => setStage(stage + 1)}>
          Next: {STAGE_TITLES[stages[stage + 1]]}
        </button>
      ) : (
        <button type="button" className="cb-btn cb-btn--primary" onClick={() => (problems.length ? setConfirming(true) : finish())}>
          Finish level {level}
        </button>
      )}
    </>
  );

  return (
    <>
      <Dialog title={`Level up to ${level}`} onClose={onClose} footer={footer}>
        <ol className="cb-levelsteps" aria-label="Level up steps">
          {stages.map((s, i) => (
            <li key={s} className={i === stage ? 'is-current' : i < stage ? 'is-done' : ''}>
              <button type="button" onClick={() => setStage(i)}>{STAGE_TITLES[s]}</button>
            </li>
          ))}
        </ol>

        {current === 'achievement' && (
          <section>
            <h3 className="cb-panel__title">Step one: tier achievement</h3>
            <p className="cb-step__guide">{ACHIEVEMENT_TEXT[level]}</p>
            <TextField label="New Experience (+2)" value={draft.experience ?? ''} maxLength={40} onChange={(experience) => update({ experience })} />
            <Chips
              label="Example Experiences"
              options={EXPERIENCE_EXAMPLES.flatMap((g) => g.items)}
              isOn={(o) => o === draft.experience}
              onPick={(experience) => update({ experience })}
            />
            <p className="cb-hint">Proficiency {before.proficiency} becomes {after.proficiency}: roll one more damage die with your weapons.</p>
            {level > 2 && markedTraits(c).length > 0 && (
              <p className="cb-hint">Marks cleared on {markedTraits(c).map(capitalize).join(' and ')}: they can be raised again.</p>
            )}
          </section>
        )}

        {current === 'advancements' && (
          <Advancements
            c={c}
            next={next}
            catalog={catalog}
            level={level}
            chosen={draft.advancements}
            picksLeft={left}
            multiclass={multiclass}
            onMulticlass={setMulticlass}
            onAdd={(o) => editAdvancements((list) => [...list, { kind: o.kind, tier: o.tier }])}
            onRemove={(i) => editAdvancements((list) => list.filter((_, k) => k !== i))}
            onChange={setAdvancement}
          />
        )}

        {current === 'thresholds' && (
          <section>
            <h3 className="cb-panel__title">Step three: damage thresholds</h3>
            <p className="cb-step__guide">Increase all damage thresholds by 1.</p>
            {after.major !== null ? (
              <div className="cb-changes">
                <p>Major threshold <strong>{before.major}</strong> becomes <strong>{after.major}</strong></p>
                <p>Severe threshold <strong>{before.severe}</strong> becomes <strong>{after.severe}</strong></p>
              </div>
            ) : (
              <p className="cb-hint">Choose armor to see your thresholds.</p>
            )}
            <LevelChanges before={before} after={after} />
          </section>
        )}

        {current === 'card' && (
          <CardStep c={c} next={next} catalog={catalog} level={level} draft={draft} onChange={update} />
        )}

        {current === 'stance' && martialForm && <StanceStep c={c} form={martialForm} draft={draft} onChange={update} />}
        {current === 'companion' && <CompanionStep c={c} draft={draft} onChange={update} />}

        {stage === stages.length - 1 && problems.length > 0 && (
          <ul className="cb-problems" aria-label="Still to choose">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}
        {stage === 0 && !edit && c.levelUps.length > 0 && (
          <p className="cb-hint">
            Made a mistake last time?{' '}
            <button type="button" className="cc-delete" onClick={undo}>Undo level {levelOf(c)}</button>
          </p>
        )}
      </Dialog>
      {confirming && (
        <Dialog
          role="alertdialog"
          title={`Finish level ${level} with choices missing?`}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <button type="button" className="cb-btn" onClick={() => setConfirming(false)}>Keep leveling</button>
              <button type="button" className="cb-btn cb-btn--primary" onClick={finish}>Yes, finish anyway</button>
            </>
          }
        >
          <p>These choices are not made yet:</p>
          <ul className="cb-problems">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <p className="cb-hint">You can finish now and make them later: the warning icon on your sheet lists them and reopens this level.</p>
        </Dialog>
      )}
    </>
  );
}

/** Everything else the new level changes on the sheet. */
function LevelChanges({ before, after }: { before: ReturnType<typeof deriveStats>; after: ReturnType<typeof deriveStats> }) {
  const rows: Array<[string, number | null, number | null]> = [
    ['Hit Points', before.hp, after.hp],
    ['Stress', before.stress, after.stress],
    ['Evasion', before.evasion, after.evasion],
    ['Proficiency', before.proficiency, after.proficiency],
  ];
  const changed = rows.filter(([, a, b]) => a !== b);
  if (!changed.length) return null;
  return (
    <div className="cb-changes">
      {changed.map(([label, a, b]) => <p key={label}>{label} <strong>{a}</strong> becomes <strong>{b}</strong></p>)}
    </div>
  );
}

interface AdvancementsProps {
  c: Character;
  next: Character;
  catalog: Catalog;
  level: number;
  chosen: Advancement[];
  picksLeft: number;
  multiclass: Multiclass;
  onMulticlass: (m: Multiclass) => void;
  onAdd: (o: AdvancementOption) => void;
  onRemove: (i: number) => void;
  onChange: (i: number, fn: (a: Advancement) => Partial<Advancement>) => void;
}

function Advancements({ c, next, catalog, level, chosen, picksLeft, multiclass, onMulticlass, onAdd, onRemove, onChange }: AdvancementsProps) {
  const options = advancementOptions(c, level, chosen);
  const tiers = Array.from(new Set(options.map((o) => o.tier)));
  const marked = level === 5 || level === 8 ? [] : markedTraits(c);
  const experiences = experienceList(next).map((e, i) => ({ ...e, i })).filter((e) => e.name);
  const upgrades = subclassCards(next, catalog);

  return (
    <section>
      <h3 className="cb-panel__title">Step two: advancements</h3>
      <p className="cb-step__guide">
        Choose any two advancements with at least one unmarked slot from your tier or below. Options with multiple slots can
        be chosen more than once. When you choose an advancement, mark one of its slots.
      </p>
      <p className="cb-counter" aria-live="polite">{picksLeft} of 2 picks left</p>

      {chosen.map((a, i) => (
        <div key={i} className="cb-chosen">
          <div className="cb-chosen__head">
            <strong>{options.find((o) => o.kind === a.kind && o.tier === a.tier)?.text}</strong>
            <button type="button" className="cb-btn" onClick={() => onRemove(i)}>Remove</button>
          </div>
          {a.kind === 'traits' && (
            <TraitRaise
              c={c}
              catalog={catalog}
              picked={a.traits ?? []}
              marked={marked}
              otherPick={chosen.flatMap((o, k) => (k !== i ? o.traits ?? [] : []))}
              onPick={(t) => onChange(i, (cur) => {
                const traits = toggle(cur.traits ?? [], t) as Trait[];
                return traits.length <= 2 ? { traits } : {};
              })}
            />
          )}
          {a.kind === 'experiences' && (
            <Chips
              label="Experiences to raise"
              options={experiences.map((e) => `${e.name} (+${e.bonus})`)}
              isOn={(o) => !!a.experiences?.includes(experiences.find((e) => `${e.name} (+${e.bonus})` === o)?.i ?? -1)}
              onPick={(o) => onChange(i, (cur) => {
                const index = experiences.find((e) => `${e.name} (+${e.bonus})` === o)?.i ?? -1;
                const picked = cur.experiences?.includes(index) ? cur.experiences.filter((x) => x !== index) : [...(cur.experiences ?? []), index];
                return picked.length <= 2 ? { experiences: picked } : {};
              })}
            />
          )}
          {a.kind === 'domainCard' && (
            <CardList
              cards={cardChoices({ ...next, levelUps: c.levelUps }, catalog, level, CARD_LEVEL_CAP[a.tier])}
              picked={a.card}
              verb="Choose"
              onPick={(card) => onChange(i, () => ({ card }))}
            />
          )}
          {a.kind === 'subclass' && upgrades.length > 1 && (
            <p className="cb-hint">You take your {upgrades[upgrades.length - 1].tier} card: {upgrades[upgrades.length - 1].features.map((f) => f.name).join(', ')}.</p>
          )}
          {a.kind === 'multiclass' && <MulticlassPicker c={c} catalog={catalog} value={multiclass} onChange={onMulticlass} />}
        </div>
      ))}

      {tiers.map((tier) => (
        <div key={tier} className="cb-advlist">
          <h4 className="cb-field__label">Tier {tier} options</h4>
          {options.filter((o) => o.tier === tier).map((o) => (
            <button
              key={o.kind}
              type="button"
              className={`cb-adv${o.crossed ? ' is-crossed' : ''}`}
              disabled={!o.available}
              onClick={() => onAdd(o)}
            >
              <span className={`cb-adv__boxes${o.wide ? ' is-wide' : ''}`} aria-hidden="true">
                {Array.from({ length: o.boxes }, (_, k) => <span key={k} className={k < o.used ? 'is-marked' : ''} />)}
              </span>
              <span className="cb-adv__text">{o.text}</span>
              {o.wide && <span className="cb-source">Takes both picks</span>}
            </button>
          ))}
        </div>
      ))}
    </section>
  );
}

function MulticlassPicker({ c, catalog, value, onChange }: { c: Character; catalog: Catalog; value: Multiclass; onChange: (m: Multiclass) => void }) {
  const cls = catalog.cls(value.className);
  return (
    <div className="cb-row">
      <label className="cb-field">
        <span className="cb-field__label">Class</span>
        <select value={value.className} onChange={(e) => onChange({ className: e.target.value, domain: '', subclass: '' })}>
          <option value="">Choose a class</option>
          {catalog.classes.filter((k) => k.name !== c.className).map((k) => <option key={k.name} value={k.name}>{k.name}</option>)}
        </select>
      </label>
      <label className="cb-field">
        <span className="cb-field__label">Domain</span>
        <select value={value.domain} disabled={!cls} onChange={(e) => onChange({ ...value, domain: e.target.value })}>
          <option value="">Choose a domain</option>
          {classDomains(cls).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>
      <label className="cb-field">
        <span className="cb-field__label">Foundation card</span>
        <select value={value.subclass} disabled={!cls} onChange={(e) => onChange({ ...value, subclass: e.target.value })}>
          <option value="">Choose a subclass</option>
          {Object.keys(cls?.subclasses ?? {}).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
    </div>
  );
}

function CardList({ cards, picked, verb, onPick }: { cards: ReturnType<typeof cardChoices>; picked?: string; verb: string; onPick: (name: string) => void }) {
  if (!cards.length) return <p className="cb-hint">No cards left to take at this level.</p>;
  return (
    <div className="cb-cardlist">
      {cards.map((k) => (
        <div key={k.name} className={`cb-deal${k.name === picked ? ' is-selected' : ''}`}>
          <DomainCardSheet card={k} />
          <button type="button" className={`cb-btn${k.name === picked ? ' cb-btn--primary' : ''}`} aria-pressed={k.name === picked} onClick={() => onPick(k.name)}>
            {k.name === picked ? `Taken: ${k.name}` : `${verb} ${k.name}`}
          </button>
        </div>
      ))}
    </div>
  );
}

function CardStep({ c, next, catalog, level, draft, onChange }: { c: Character; next: Character; catalog: Catalog; level: number; draft: LevelUp; onChange: (patch: Partial<LevelUp>) => void }) {
  const without = { ...next, levelUps: [...c.levelUps, { ...draft, card: '', swap: undefined }] };
  const owned = ownedCards(without);
  const swapFrom = catalog.card(draft.swap?.from ?? '');
  const swapChoices = swapFrom ? cardChoices(without, catalog, swapFrom.level).filter((k) => k.name !== draft.card) : [];
  return (
    <section>
      <h3 className="cb-panel__title">Step four: domain card</h3>
      <p className="cb-step__guide">
        Acquire a new domain card at your level or lower from one of your class's domains and add it to your loadout or vault.
        {c.multiclass && ` You can also choose from ${c.multiclass.domain} cards at or below half your level (rounded up).`}
      </p>
      <CardList cards={cardChoices(without, catalog, level)} picked={draft.card} verb="Take" onPick={(card) => onChange({ card })} />
      <details className="cb-group">
        <summary>Exchange a card (optional)</summary>
        <p className="cb-hint">You can also exchange one domain card you've previously acquired for a different domain card of the same level or lower.</p>
        <div className="cb-row">
          <label className="cb-field">
            <span className="cb-field__label">Give up</span>
            <select value={draft.swap?.from ?? ''} onChange={(e) => onChange({ swap: e.target.value ? { from: e.target.value, to: '' } : undefined })}>
              <option value="">Keep all my cards</option>
              {owned.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label className="cb-field">
            <span className="cb-field__label">Take instead</span>
            <select value={draft.swap?.to ?? ''} disabled={!swapFrom} onChange={(e) => draft.swap && onChange({ swap: { ...draft.swap, to: e.target.value } })}>
              <option value="">Choose a card</option>
              {swapChoices.map((k) => <option key={k.name} value={k.name}>{k.name} (level {k.level})</option>)}
            </select>
          </label>
        </div>
      </details>
    </section>
  );
}

/** The Ranger Companion sheet's level up: a new Experience at tier achievements, then one Training option. */
interface TraitRaiseProps {
  c: Character;
  catalog: Catalog;
  picked: Trait[];
  marked: Trait[];
  /** Traits the level's other trait advancement raises. */
  otherPick: Trait[];
  onPick: (t: Trait) => void;
}

/**
 * "+1 to two unmarked traits" on the sheet's own trait row: each trait's value now, the class's suggested value and the
 * spellcast trait, so the player never goes back to look them up; marked traits wait for the next tier achievement.
 */
function TraitRaise({ c, catalog, picked, marked, otherPick, onPick }: TraitRaiseProps) {
  const now = deriveStats({ ...c, beastform: undefined }, catalog).traits;
  const suggestion = catalog.cls(c.className)?.stats?.suggestedTraits;
  const suggested = suggestion ? parseTraits(suggestion) : undefined;
  const spellTrait = spellcastTrait(c, catalog);
  return (
    <div className="cs-tokens">
      <div className="cb-traits cb-traits--raise" role="group" aria-label="Traits to raise">
        {TRAITS.map((t) => {
          const on = picked.includes(t);
          const isMarked = marked.includes(t);
          const isOther = otherPick.includes(t);
          const label = [
            `${capitalize(t)} ${signed(now[t])}`,
            isMarked && 'marked',
            isOther && 'raised by your other pick',
            suggested && `class suggests ${signed(suggested[t])}`,
            t === spellTrait && 'spellcast trait',
          ].filter(Boolean).join(', ');
          const status = on ? `Becomes ${signed((now[t] ?? 0) + 1)}` : isMarked ? 'Marked' : isOther ? 'Other pick' : '';
          return (
            <TraitButton
              key={t}
              trait={t}
              value={signed(now[t])}
              marked={isMarked}
              label={label}
              pressed={on}
              disabled={!on && (isMarked || isOther || picked.length >= 2)}
              onClick={() => onPick(t)}
            >
              {suggested && <span className="cb-trait__note">Suggested {signed(suggested[t])}</span>}
              {status && <span className={`cb-trait__note${on ? ' is-on' : ''}`}>{status}</span>}
              {t === spellTrait && <span className="cb-source" aria-hidden="true">Spellcast trait</span>}
            </TraitButton>
          );
        })}
      </div>
    </div>
  );
}

/** Stance Fighter: one more martial stance, from the new level's tier or lower; known ones stay marked. */
function StanceStep({ c, form, draft, onChange }: { c: Character; form: MartialFormData; draft: LevelUp; onChange: (patch: Partial<LevelUp>) => void }) {
  const known = knownStances(c);
  const tiers = Object.entries(form.tiers).filter(([tier]) => Number(tier) <= tierOf(draft.level));
  return (
    <section>
      <h3 className="cb-panel__title">Your new martial stance</h3>
      <p className="cb-step__guide">Choose an additional stance from your tier or lower.</p>
      {tiers.map(([tier, stances]) => (
        <Fragment key={tier}>
          <p className="cb-field__label">Tier {tier}</p>
          <div className="cb-grid cb-grid--two">
            {stances.map((s) => {
              const had = known.includes(s.name);
              const on = draft.stance === s.name;
              return (
                <Choice key={s.name} selected={had || on} disabled={had} onClick={() => onChange({ stance: on ? undefined : s.name })}>
                  <span className="cb-choice__title">{s.name}{had && ' (known)'}</span>
                  <span className="cb-choice__text">{s.description}</span>
                </Choice>
              );
            })}
          </div>
        </Fragment>
      ))}
    </section>
  );
}

function CompanionStep({ c, draft, onChange }: { c: Character; draft: LevelUp; onChange: (patch: Partial<LevelUp>) => void }) {
  const t = draft.companion;
  const before = companionStats({ ...c, levelUps: [...c.levelUps, { ...draft, companion: undefined }] });
  const after = companionStats({ ...c, levelUps: [...c.levelUps, draft] });
  const experiences = before.experiences.map((e, i) => ({ ...e, i, label: `${e.name} (+${e.bonus})` })).filter((e) => e.name);
  const train = (companion?: CompanionTraining) => onChange({ companion });
  const steps = [
    ...(before.damage !== COMPANION_DICE[COMPANION_DICE.length - 1] ? [{ step: 'damage' as const, label: `Damage: ${before.damage} to ${COMPANION_DICE[COMPANION_DICE.indexOf(before.damage) + 1]}` }] : []),
    ...(before.range !== COMPANION_RANGES[COMPANION_RANGES.length - 1] ? [{ step: 'range' as const, label: `Range: ${before.range} to ${COMPANION_RANGES[COMPANION_RANGES.indexOf(before.range) + 1]}` }] : []),
  ];
  const changes: Array<[string, string | number, string | number]> = [
    ['Evasion', before.evasion, after.evasion],
    ['Stress slots', before.stress, after.stress],
    ['Damage die', before.damage, after.damage],
    ['Range', before.range, after.range],
  ];
  const changed = changes.filter(([, a, b]) => a !== b);

  return (
    <section>
      <h3 className="cb-panel__title">Your companion</h3>
      {TIER_ACHIEVEMENT_LEVELS.includes(draft.level) && (
        <>
          <p className="cb-step__guide">{COMPANION_SHEET.experience}</p>
          <TextField
            label="New Companion Experience (+2)"
            value={draft.companionExperience ?? ''}
            maxLength={40}
            onChange={(companionExperience) => onChange({ companionExperience })}
          />
          <Chips
            label="Example Companion Experiences"
            options={COMPANION_SHEET.examples}
            isOn={(o) => o === draft.companionExperience}
            onPick={(companionExperience) => onChange({ companionExperience })}
          />
        </>
      )}
      <p className="cb-step__guide">{COMPANION_SHEET.training}</p>
      <div className="cb-advlist">
        {trainingOptions(c).map((o) => {
          const on = t?.option === o.name;
          return (
            <Fragment key={o.name}>
              <button
                type="button"
                className={`cb-adv${on ? ' is-selected' : ''}`}
                aria-pressed={on}
                disabled={!on && !o.available}
                onClick={() => train(on ? undefined : { option: o.name })}
              >
                <span className="cb-adv__boxes" aria-hidden="true">
                  {Array.from({ length: o.boxes }, (_, k) => <span key={k} className={k < o.used + (on ? 1 : 0) ? 'is-marked' : ''} />)}
                </span>
                <span className="cb-adv__text"><strong>{o.name}:</strong> {o.text}</span>
              </button>
              {on && t.option === 'Intelligent' && (
                <Chips
                  label="Companion Experience to raise"
                  options={experiences.map((e) => e.label)}
                  isOn={(x) => experiences.find((e) => e.label === x)?.i === t.experience}
                  onPick={(x) => train({ ...t, experience: experiences.find((e) => e.label === x)?.i })}
                />
              )}
              {on && t.option === 'Vicious' && (
                <Chips
                  label="Vicious step"
                  options={steps.map((s) => s.label)}
                  isOn={(x) => steps.find((s) => s.label === x)?.step === t.step}
                  onPick={(x) => train({ ...t, step: steps.find((s) => s.label === x)?.step })}
                />
              )}
            </Fragment>
          );
        })}
      </div>
      {changed.length > 0 && (
        <div className="cb-changes">
          {changed.map(([label, a, b]) => <p key={label}>{label} <strong>{a}</strong> becomes <strong>{b}</strong></p>)}
        </div>
      )}
    </section>
  );
}
